import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  FormControlLabel, IconButton, Menu, MenuItem, ListItemIcon, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import WorkspacesOutlinedIcon from '@mui/icons-material/WorkspacesOutlined';
import LayersClearOutlinedIcon from '@mui/icons-material/LayersClearOutlined';
import { normalizeActionLogicalGroup } from './actionFlowLogicalGroups';
import { explainProjectDag } from '../../Utils/actionsApi';
import DagHelpConversation from './DagHelpConversation';
import DialogHeading from '../Common/DialogHeading';

const MAX_SELECTION = 24;
const EXPLAINABLE_KEY = /^(?:center|reanalysis:project|action:\d+|flow:\d+|input:[a-z0-9]+:\d+|preparation:input:[a-z0-9]+:\d+:(?:step-[A-Za-z0-9_-]{1,64}|native-[a-z0-9-]{1,64}))$/;
const explanationKeysForNode = (node) => {
  if (node.type === 'logicalGroupAnchor' && node.id?.startsWith('logical-group:')) {
    const keys = node.data?.helpNodeKeys;
    return Array.isArray(keys) && keys.every((key) => typeof key === 'string' && EXPLAINABLE_KEY.test(key)) ? keys : [];
  }
  return EXPLAINABLE_KEY.test(node.id) ? [node.id] : [];
};
const logicalGroupIdsForSelection = (selection) => [...new Set(selection.map((node) => (
  node.type === 'logicalGroupAnchor' && explanationKeysForNode(node).length
    ? node.id.slice('logical-group:'.length)
    : normalizeActionLogicalGroup(node.data?.node?.logicalGroup)?.id
)).filter(Boolean))];

const groupedActionIdsForSelection = (selection) => [...new Set(selection.flatMap((node) => {
  if (node.type === 'logicalGroupAnchor') return explanationKeysForNode(node).filter((key) => /^action:\d+$/.test(key));
  return normalizeActionLogicalGroup(node.data?.node?.logicalGroup) && /^action:\d+$/.test(node.id) ? [node.id] : [];
}))].map((key) => Number(key.slice('action:'.length)));

export const helpSelectionNodeKeys = (nodes) => [...new Set(nodes.flatMap(explanationKeysForNode))];
export const explainableDagNodes = (nodes) => nodes.filter((node) => (
  !node.hidden && !node.data?.node?.assistantPreview && explanationKeysForNode(node).length > 0
));

// Hit testing runs once on release, never during pointer movement. Graph state is untouched.
export const nodesInHelpRectangle = (nodes, start, end, toFlowPosition) => {
  const first = toFlowPosition(start);
  const last = toFlowPosition(end);
  const left = Math.min(first.x, last.x);
  const right = Math.max(first.x, last.x);
  const top = Math.min(first.y, last.y);
  const bottom = Math.max(first.y, last.y);
  return explainableDagNodes(nodes).filter((node) => {
    const position = node.positionAbsolute || node.position;
    const width = node.measured?.width || node.width || 154;
    const height = node.measured?.height || node.height || 150;
    return position.x <= right && position.x + width >= left
      && position.y <= bottom && position.y + height >= top;
  });
};

export default function DagHelp({ canvasRef, reactFlowInstance, nodes, caseid, locale, disabled, closeOnDisable = false, onActiveChange, onPrepareCorrection, onCreateLogicalGroup, onUngroupLogicalGroups, boxCreation, t }) {
  const [menuPosition, setMenuPosition] = useState(null);
  const [boxSubmitted, setBoxSubmitted] = useState(false);
  const [boxName, setBoxName] = useState('');
  const [namingBox, setNamingBox] = useState(false);
  const [active, setActive] = useState(false);
  const [rectangle, setRectangle] = useState(null);
  const [picker, setPicker] = useState(false);
  const [query, setQuery] = useState('');
  const [selection, setSelection] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const drag = useRef(null);
  const request = useRef(null);
  const button = useRef(null);

  useEffect(() => { onActiveChange?.(active); }, [active, onActiveChange]);

  const close = () => {
    request.current?.abort();
    request.current = null;
    drag.current = null;
    setMenuPosition(null);
    setBoxSubmitted(false);
    setNamingBox(false);
    setActive(false);
    setRectangle(null);
    setPicker(false);
    setDialogOpen(false);
    setSelection([]);
    setResult(null);
    setError('');
    setLoading(false);
    if (active || picker || dialogOpen) button.current?.focus();
  };

  useEffect(() => {
    // Disable all graph controls, including their keyboard shortcuts, while selecting.
    // The separate overlay and accessible picker do not modify React Flow's selection.
    const flowRoot = canvasRef.current?.querySelector('.react-flow');
    const graphElements = flowRoot
      ? Array.from(flowRoot.children).filter((element) => !element.contains(button.current))
      : Array.from(canvasRef.current?.querySelectorAll('.react-flow__renderer') || []);
    if (!active) return undefined;
    const previous = graphElements.map((element) => [element, element.inert]);
    graphElements.forEach((element) => { element.inert = true; });
    return () => previous.forEach(([element, inert]) => { element.inert = inert; });
  }, [active, canvasRef]);

  useEffect(() => {
    close();
    return () => { request.current?.abort(); request.current = null; };
    // A response for a previous project must never appear in the current one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseid]);

  useEffect(() => {
    // Busy canvas controls must not discard an open selection or conversation.
    // Only a deliberate transition to another editor closes this session.
    if (closeOnDisable) close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeOnDisable]);

  useEffect(() => {
    if (!active) return undefined;
    const escape = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); close(); }
    };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const chooseSelection = (chosen, position) => {
    const count = helpSelectionNodeKeys(chosen).length;
    const groupIds = logicalGroupIdsForSelection(chosen);
    const selectedActionCount = groupedActionIdsForSelection(chosen).length;
    const canUngroupSelection = onUngroupLogicalGroups && groupIds.length > 0 && groupIds.length <= MAX_SELECTION
      && selectedActionCount > 0 && selectedActionCount <= 500;
    if (!count || (count > MAX_SELECTION && !canUngroupSelection)) {
      setError(t(count ? 'dag.help.too_many' : 'dag.help.empty'));
      return;
    }
    setSelection(chosen);
    setPicker(false);
    setError('');
    const bounds = button.current?.getBoundingClientRect();
    setMenuPosition(position || { top: bounds?.bottom || 80, left: bounds?.left || 16 });
  };

  const explain = async (chosen) => {
    const nodeKeys = helpSelectionNodeKeys(chosen);
    if (!nodeKeys.length || nodeKeys.length > MAX_SELECTION) {
      setError(t(nodeKeys.length ? 'dag.help.too_many' : 'dag.help.empty'));
      return;
    }
    setMenuPosition(null);
    setNamingBox(false);
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setSelection(chosen);
    setPicker(false);
    setDialogOpen(true);
    setResult(null);
    setError('');
    setLoading(true);
    const timeout = setTimeout(() => controller.abort(), 120000);
    try {
      const response = await explainProjectDag(caseid, { node_keys: nodeKeys, language: locale || 'en' }, controller.signal);
      if (request.current !== controller) return;
      setResult(response);
    } catch (failure) {
      if (request.current === controller) {
        const code = failure?.payload?.error?.code || failure?.code;
        setError(t(['selection_unavailable', 'selection_needs_review'].includes(code) ? `dag.help.${code}` : 'dag.help.error'));
      }
    } finally {
      clearTimeout(timeout);
      if (request.current === controller) setLoading(false);
    }
  };

  useEffect(() => {
    if (boxSubmitted && boxCreation?.success) close();
    // The result belongs to the submitted selection; keep its name on failure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxCreation, boxSubmitted]);

  const available = picker ? explainableDagNodes(nodes) : [];
  const selectedNodeKeys = helpSelectionNodeKeys(selection);
  const selectedActions = selection.filter((node) => /^action:\d+$/.test(node.id));
  const canCreateBox = Boolean(onCreateLogicalGroup && selectedActions.length
    && selection.every((node) => /^(action|flow):\d+$/.test(node.id))
    && selectedActions.every((node) => !node.data?.node?.logicalGroup));
  const selectedGroupIds = logicalGroupIdsForSelection(selection);
  const selectedGroupedActionIds = groupedActionIdsForSelection(selection);
  const canUngroup = Boolean(onUngroupLogicalGroups && selectedGroupIds.length && selectedGroupIds.length <= MAX_SELECTION
    && selectedGroupedActionIds.length > 0 && selectedGroupedActionIds.length <= 500);
  const groupChangePending = Boolean(boxCreation?.pending);
  const label = (node) => node?.id === 'center' ? t('dag.help.project_data')
    : node?.data?.node?.label || node?.ariaLabel || node?.data?.title || t('dag.help.unnamed');
  const layer = active && canvasRef.current && createPortal(
    <Box sx={{ position: 'absolute', inset: '56px 0 0', zIndex: 20 }}>
      <Box
        data-testid="dag-help-selection"
        aria-label={t('dag.help.select_label')}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          event.preventDefault();
          event.currentTarget.setPointerCapture?.(event.pointerId);
          drag.current = { x: event.clientX, y: event.clientY };
          setRectangle(null);
          setError('');
        }}
        onPointerMove={(event) => {
          if (!drag.current) return;
          const bounds = event.currentTarget.getBoundingClientRect();
          setRectangle({ left: Math.min(drag.current.x, event.clientX) - bounds.left,
            top: Math.min(drag.current.y, event.clientY) - bounds.top,
            width: Math.abs(event.clientX - drag.current.x), height: Math.abs(event.clientY - drag.current.y) });
        }}
        onPointerUp={(event) => {
          if (!drag.current) return;
          const start = drag.current;
          drag.current = null;
          setRectangle(null);
          event.currentTarget.releasePointerCapture?.(event.pointerId);
          if (reactFlowInstance) chooseSelection(nodesInHelpRectangle(reactFlowInstance.getNodes(), start,
            { x: event.clientX, y: event.clientY }, reactFlowInstance.screenToFlowPosition), { top: event.clientY, left: event.clientX });
        }}
        onPointerCancel={() => { drag.current = null; setRectangle(null); }}
        sx={{ position: 'absolute', inset: 0, cursor: 'crosshair', touchAction: 'none' }}
      >
        {rectangle && <Box sx={{ position: 'absolute', ...rectangle, pointerEvents: 'none',
          border: '2px solid', borderColor: 'primary.main', bgcolor: 'rgba(87,87,176,.12)', borderRadius: 1 }} />}
      </Box>
      <Stack alignItems="center" sx={{ position: 'absolute', top: 8, left: 8, right: 8, pointerEvents: 'none' }}>
        <Box sx={{ bgcolor: 'background.paper', boxShadow: 3, borderRadius: 2, px: 2, py: 1,
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, pointerEvents: 'auto', maxWidth: '100%' }}>
          <Typography variant="body2" role="status">{error || t('dag.help.hint')}</Typography>
          <Button size="small" onClick={() => { setPicker(true); setQuery(''); setError(''); }}>{t('dag.help.choose')}</Button>
          <IconButton size="small" aria-label={t('common.cancel')} onClick={close}><CloseRoundedIcon fontSize="small" /></IconButton>
        </Box>
      </Stack>
    </Box>, canvasRef.current
  );

  return <>
    <Tooltip title={t('dag.help.title')}>
      <span><IconButton ref={button} aria-label={t('dag.help.title')} aria-pressed={active}
        disabled={disabled || !caseid || !reactFlowInstance} onClick={() => active ? close() : setActive(true)}
        sx={{ width: 36, height: 36, bgcolor: active ? 'primary.main' : 'background.paper',
          color: active ? 'primary.contrastText' : 'primary.main', border: '1px solid', borderColor: 'divider',
          '&:hover': { bgcolor: active ? 'primary.dark' : 'action.hover' } }}>
        <HelpOutlineRoundedIcon fontSize="small" />
      </IconButton></span>
    </Tooltip>
    {layer}
    <Dialog open={picker} onClose={close} fullWidth maxWidth="sm" aria-labelledby="dag-help-picker-title">
      <DialogHeading id="dag-help-picker-title" icon={HelpOutlineRoundedIcon}
        title={t('dag.help.choose')} description={t('dag.help.hint')} />
      <DialogContent>
        {error && <Alert severity="warning">{error}</Alert>}
        <TextField autoFocus size="small" fullWidth label={t('dag.help.search')} value={query} onChange={(event) => setQuery(event.target.value)} sx={{ my: 1 }} />
        <Stack spacing={0.75}>
          {available.filter((node) => label(node).toLocaleLowerCase().includes(query.toLocaleLowerCase())).map((node) => (
            <FormControlLabel key={node.id} label={label(node)} sx={{ m: 0, px: 1, py: 0.5,
              border: '1px solid', borderColor: selection.some((chosen) => chosen.id === node.id) ? 'primary.light' : 'divider',
              borderRadius: '12px', bgcolor: selection.some((chosen) => chosen.id === node.id) ? 'action.selected' : 'background.paper',
              '& .MuiFormControlLabel-label': { overflowWrap: 'anywhere', minWidth: 0 },
              '&:hover': { bgcolor: 'action.hover' } }} control={<Checkbox
              checked={selection.some((chosen) => chosen.id === node.id)}
              disabled={selectedNodeKeys.length >= MAX_SELECTION && !selection.some((chosen) => chosen.id === node.id)}
              onChange={(_event, checked) => setSelection((current) => checked ? [...current, node] : current.filter((chosen) => chosen.id !== node.id))}
            />} />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={close}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!selection.length} onClick={() => chooseSelection(selection)}>{t('dag.help.continue')}</Button></DialogActions>
    </Dialog>
    <Menu open={Boolean(menuPosition)} onClose={(_event, reason) => {
      if (reason === 'escapeKeyDown') close();
      else setMenuPosition(null);
    }}
      anchorReference="anchorPosition" anchorPosition={menuPosition || undefined}
      MenuListProps={{ 'aria-label': t('dag.help.selection_actions') }}
      PaperProps={{ sx: { minWidth: 200, borderRadius: '14px', mt: 0.5 } }}>
      <MenuItem disabled={!canCreateBox || groupChangePending} onClick={() => {
        setMenuPosition(null);
        setNamingBox(true);
        setBoxSubmitted(false);
        setBoxName(t('dag.help.box_default'));
        setResult(null);
        setError('');
        setDialogOpen(true);
      }}>
        <ListItemIcon><WorkspacesOutlinedIcon fontSize="small" /></ListItemIcon>{t('dag.help.create_box')}
      </MenuItem>
      {canUngroup && <MenuItem disabled={groupChangePending} onClick={() => {
        if (onUngroupLogicalGroups(selectedGroupIds, selectedGroupedActionIds) !== false) {
          if (boxCreation === undefined) close();
          else setBoxSubmitted(true);
        }
      }}>
        <ListItemIcon><LayersClearOutlinedIcon fontSize="small" /></ListItemIcon>{t('dag.help.ungroup', { count: selectedGroupIds.length })}
        {groupChangePending && <CircularProgress size={14} sx={{ ml: 1 }} />}
      </MenuItem>}
      {boxSubmitted && boxCreation?.operation === 'ungroup' && !groupChangePending && !boxCreation.success
        && <Box component="li" sx={{ listStyle: 'none', px: 1, maxWidth: 280 }}>
          <Alert severity="warning">{t('dag.help.ungroup_error')}</Alert>
        </Box>}
      <MenuItem disabled={groupChangePending || selectedNodeKeys.length > MAX_SELECTION} onClick={() => explain(selection)}>
        <ListItemIcon><ChatBubbleOutlineRoundedIcon fontSize="small" /></ListItemIcon>{t('dag.help.chat')}
      </MenuItem>
    </Menu>
    <Dialog open={dialogOpen} onClose={close} fullWidth maxWidth="sm" aria-labelledby="dag-help-result-title">
      <DialogHeading id="dag-help-result-title" icon={HelpOutlineRoundedIcon}
        title={t(namingBox ? 'dag.help.create_box' : 'dag.help.title')} description={namingBox ? undefined : t('dag.help.conversation')} />
      <DialogContent aria-busy={loading} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0,
        overflow: result ? 'hidden' : 'auto', height: result ? 'min(520px, 65vh)' : undefined }}>
        {loading && <Stack direction="row" alignItems="center" spacing={1.5} role="status" sx={{ p: 2.5,
          borderRadius: '14px', bgcolor: 'action.hover' }}><CircularProgress size={22} /><Typography>{t('dag.help.loading')}</Typography></Stack>}
        {error && <Alert severity="warning" action={<Button onClick={() => explain(selection)}>{t('dag.help.retry')}</Button>}>{error}</Alert>}
        {result && <DagHelpConversation caseid={caseid} locale={locale} nodeKeys={selectedNodeKeys}
          explanation={result} open={dialogOpen} t={t} onPrepareCorrection={onPrepareCorrection ? (proposal) => {
            if (onPrepareCorrection(proposal) === false) return false;
            close();
            return true;
          } : undefined} />}
      </DialogContent>
      {boxSubmitted && boxCreation && !boxCreation.pending && !boxCreation.success && <Alert severity="warning" sx={{ mx: 3 }}>{t('dag.help.box_error')}</Alert>}
      {canCreateBox && namingBox && <Box sx={{ px: 3, pt: 1 }}>
        <TextField autoFocus fullWidth size="small" label={t('dag.help.box_name')} value={boxName}
          inputProps={{ maxLength: 4000 }} onChange={(event) => setBoxName(event.target.value)} />
      </Box>}
      <DialogActions>
        {canCreateBox && namingBox && <Button variant="contained" onClick={() => {
          if (boxName.trim() && onCreateLogicalGroup(selectedActions.map((node) => Number(node.id.split(':')[1])), boxName.trim()) !== false) {
            if (boxCreation === undefined) close();
            else setBoxSubmitted(true);
          }
        }} disabled={Boolean(boxCreation?.pending) || (namingBox && !boxName.trim())}>{t('dag.help.create_box')}</Button>}
        <Button variant="outlined" onClick={() => { request.current?.abort(); request.current = null; setDialogOpen(false); setSelection([]); setBoxSubmitted(false); setNamingBox(false); setError(''); }}>{t('dag.help.another')}</Button>
        <Button variant={namingBox ? "text" : "contained"} onClick={close}>{t(namingBox ? 'common.cancel' : 'common.close')}</Button>
      </DialogActions>
    </Dialog>
  </>;
}
